// src/app/data/services/version.service.ts
import { Injectable, NgZone } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { APP_VERSION } from 'src/environments/version';

/** Cada cuánto se revisa si hay versión nueva. */
const INTERVALO_MS = 5 * 60 * 1000;      // 5 minutos
/** Si eligen "Más tarde", cuánto esperar para volver a avisar. */
const POSPONER_MS = 10 * 60 * 1000;      // 10 minutos

/**
 * Avisa en la versión WEB de la app (streamyapp.info) cuando hay
 * una versión más nueva publicada. Compara la versión compilada
 * (APP_VERSION) con la publicada en assets/version.json.
 *
 * En la app instalada (Android / iPhone) no hace nada: ahí el código
 * viene dentro de la app y se actualiza desde la tienda.
 */
@Injectable({ providedIn: 'root' })
export class VersionService {

  private avisoAbierto = false;
  private pospuestoHasta = 0;
  private iniciado = false;

  constructor(private zone: NgZone, private alertCtrl: AlertController) {}

  iniciar(): void {
    // En desarrollo (ionic serve) o en la app instalada no se revisa
    if (this.iniciado || APP_VERSION === 'dev' || Capacitor.isNativePlatform()) { return; }
    this.iniciado = true;

    // Fuera de la zona de Angular para no disparar detección de cambios cada 5 min
    this.zone.runOutsideAngular(() => {
      setInterval(() => this.revisar(), INTERVALO_MS);

      // Al volver a la pestaña (lo más común: la dejaron abierta de un día para otro)
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') { this.revisar(); }
      });
    });

    this.revisar();
  }

  private async revisar(): Promise<void> {
    if (this.avisoAbierto || Date.now() < this.pospuestoHasta) { return; }

    try {
      // ?t= y no-store evitan que el navegador use una copia guardada
      const respuesta = await fetch(`assets/version.json?t=${Date.now()}`, { cache: 'no-store' });
      if (!respuesta.ok) { return; }

      const { version } = await respuesta.json();
      if (version && version !== 'dev' && version !== APP_VERSION) {
        this.zone.run(() => this.mostrarAviso());
      }
    } catch {
      // Sin internet o error del servidor: se reintenta en la próxima revisión
    }
  }

  private async mostrarAviso(): Promise<void> {
    this.avisoAbierto = true;

    const alerta = await this.alertCtrl.create({
      cssClass: 'alerta-version',
      header: 'Hay una nueva versión',
      message: 'Estás usando una versión anterior de StreamyApp. Actualiza para ver los últimos cambios.',
      backdropDismiss: false,
      buttons: [
        { text: 'Más tarde', role: 'cancel', cssClass: 'alerta-version-tarde' },
        { text: 'Actualizar ahora', role: 'confirm', cssClass: 'alerta-version-ok' },
      ],
    });

    await alerta.present();
    const { role } = await alerta.onDidDismiss();
    this.avisoAbierto = false;

    if (role === 'confirm') {
      window.location.reload();
    } else {
      this.pospuestoHasta = Date.now() + POSPONER_MS;
    }
  }
}